import { Box, Grid, Group, Paper, ScrollArea, Select, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconCheck, IconPalette } from '@tabler/icons-react';
import { forwardRef } from 'react';
import { usePreferencesStore } from '../../../stores';
import { colorSchemes, getGradientStyle } from '../../../theme/theme';

export type ColorSchemeSwitcherVariant = 'select' | 'grid' | 'pills' | 'compact';

export interface ColorSchemeSwitcherProps {
  variant?: ColorSchemeSwitcherVariant;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  columns?: number;
  maxHeight?: number;
}

const ColorSchemeSwitcher = forwardRef<HTMLDivElement, ColorSchemeSwitcherProps>(
  ({ variant = 'select', size = 'sm', columns = 5, maxHeight = 300 }, ref) => {
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const setColorScheme = usePreferencesStore((state) => state.setColorScheme);

    const options = colorSchemes.map((scheme) => ({
      value: scheme.key,
      label: `${scheme.emoji} ${scheme.label}`,
    }));

    if (variant === 'select') {
      return (
        <Select
          ref={ref as any}
          value={colorSchemeKey}
          onChange={(value) => value && setColorScheme(value)}
          data={options}
          size={size}
          leftSection={<IconPalette size={16} />}
          searchable
        />
      );
    }

    if (variant === 'compact') {
      return (
        <ScrollArea ref={ref as any} h={40} type="never">
          <Group gap={4} wrap="nowrap">
            {colorSchemes.map((scheme) => (
              <Tooltip key={scheme.key} label={scheme.label}>
                <UnstyledButton
                  onClick={() => setColorScheme(scheme.key)}
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: getGradientStyle(scheme.primary),
                    border: scheme.key === colorSchemeKey ? '2px solid var(--mantine-color-blue-6)' : '2px solid transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {scheme.key === colorSchemeKey && <IconCheck size={12} color="white" />}
                </UnstyledButton>
              </Tooltip>
            ))}
          </Group>
        </ScrollArea>
      );
    }

    if (variant === 'pills') {
      return (
        <ScrollArea ref={ref as any} h={maxHeight} type="auto">
          <Group gap="xs">
            {colorSchemes.map((scheme) => (
              <UnstyledButton
                key={scheme.key}
                onClick={() => setColorScheme(scheme.key)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '16px',
                  background: getGradientStyle(scheme.primary),
                  color: 'white',
                  fontWeight: 500,
                  fontSize: size === 'xs' ? 10 : size === 'sm' ? 12 : 14,
                  border: scheme.key === colorSchemeKey ? '2px solid var(--mantine-color-blue-6)' : '2px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {scheme.emoji} {scheme.label}
                {scheme.key === colorSchemeKey && <IconCheck size={14} />}
              </UnstyledButton>
            ))}
          </Group>
        </ScrollArea>
      );
    }

    // Grid variant
    return (
      <ScrollArea ref={ref as any} h={maxHeight} type="auto">
        <Grid gutter="sm">
          {colorSchemes.map((scheme) => (
            <Grid.Col key={scheme.key} span={12 / columns}>
              <Paper
                p="sm"
                radius="md"
                withBorder
                onClick={() => setColorScheme(scheme.key)}
                style={{
                  cursor: 'pointer',
                  borderColor: scheme.key === colorSchemeKey ? 'var(--mantine-primary-color-filled)' : undefined,
                  borderWidth: scheme.key === colorSchemeKey ? 2 : 1,
                  transition: 'all 0.2s ease',
                }}
              >
                <Group gap="sm">
                  <Box
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      background: getGradientStyle(scheme.primary),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {scheme.key === colorSchemeKey && <IconCheck size={20} color="white" />}
                  </Box>
                  <Box>
                    <Text size="sm" fw={500}>
                      {scheme.emoji} {scheme.label}
                    </Text>
                    <Group gap={4}>
                      <Box
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          background: scheme.primary.from,
                        }}
                      />
                      <Box
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          background: scheme.secondary.from,
                        }}
                      />
                      <Box
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          background: scheme.tertiary.from,
                        }}
                      />
                    </Group>
                  </Box>
                </Group>
              </Paper>
            </Grid.Col>
          ))}
        </Grid>
      </ScrollArea>
    );
  }
);

ColorSchemeSwitcher.displayName = 'ColorSchemeSwitcher';

export default ColorSchemeSwitcher;
